import { supabase } from '@/lib/supabase';
import { BLOCK_CATALOG, type ThemeBlock } from '@/lib/themeBlocks';
import { logAiUsage, readUsage } from '@/lib/aiUsageLog';
import { htmlToText } from '@/lib/display';

// ============================================================
// Conversion assistée par IA : page HTML existante → blocs React.
//
// L'IA reçoit le code HTML d'une page (déjà présent dans le thème)
// et le traduit en une liste de BLOCS connus du moteur de rendu
// (hero, produits, témoignages, cta, onglets…). Le résultat est un
// schéma de blocs valide, éditable ensuite dans l'éditeur visuel.
// ============================================================

const XAI_ENDPOINT = 'https://api.x.ai/v1/chat/completions';
const XAI_MODEL = 'grok-4.6';

const KNOWN_TYPES = new Set(BLOCK_CATALOG.map((b) => b.type));

function truncate(value: string, max: number): string {
  if (!value) return '';
  return value.length > max ? `${value.slice(0, max)}\n<!-- ... contenu tronqué ... -->` : value;
}

function buildCatalogPrompt(): string {
  return BLOCK_CATALOG
    .map((b) => `- "${b.type}" : ${b.label} → ${b.description}`)
    .join('\n');
}

function buildSystemPrompt(): string {
  return `Tu es un expert en intégration web. Tu convertis une page HTML existante en une liste de BLOCS réutilisables.

🎯 OBJECTIF
Analyser le HTML fourni et reproduire sa STRUCTURE et son CONTENU avec les blocs disponibles, en conservant l'ordre, l'intention et la hiérarchie des sections.

🧩 BLOCS DISPONIBLES (utilise UNIQUEMENT ces types) :
${buildCatalogPrompt()}

📐 RÈGLES
- Détecte chaque grande section de la page (bannière, grille de produits, services, galerie, avis, FAQ, formulaire, appel à l'action, texte, image…).
- Chaque section devient un bloc du bon type.
- Pour les contenus dynamiques (produits, services, témoignages, galerie, blog, équipe, FAQ, formulaires, marketplace…), utilise le bloc dynamique correspondant, SANS recopier les données.
- Pour les titres et paragraphes statiques, utilise les blocs "heading" et "text" (le champ "html" accepte du HTML simple).
- Pour la bannière (hero) : reprends le titre principal et le sous-titre, et ajoute les boutons s'ils existent (champ ctaLabel/ctaHref).
- Conserve les balises Zifek existantes ({{site_name}}, {{products}}, {{site_about}}…) si elles sont présentes dans le HTML.
- Pour un bandeau de rassurance (paiement, livraison…), utilise "trust_banner".
- Pour du contenu organisé en panneaux/onglets, utilise "tabs".
- N'invente PAS de type de bloc. Ignore ce qui ne correspond à aucun bloc.
- Chaque bloc DOIT avoir la forme { "type": "...", "props": { ... } }.

📤 FORMAT DE RÉPONSE
Réponds UNIQUEMENT avec cet objet JSON, sans texte avant ni après :
{
  "blocks": [
    { "type": "hero", "props": { "title": "...", "subtitle": "...", "ctaLabel": "...", "ctaHref": "/..." } },
    { "type": "products", "props": { "limit": 6 } },
    ...
  ]
}`;
}

function buildUserContent(html: string, pageLabel: string): string {
  return `Page à convertir : ${pageLabel || 'page'}

===== HTML EXISTANT =====
${truncate(html, 14000)}

Convertis cette page en blocs. Renvoie le JSON { "blocks": [ ... ] }.`;
}

interface RawBlock {
  type: string;
  props?: unknown;
}

/** Nettoie la réponse IA : ne garde que des blocs connus avec des props objets. */
function sanitizeBlocks(raw: unknown): ThemeBlock[] {
  const list: RawBlock[] = Array.isArray(raw)
    ? (raw as RawBlock[])
    : (raw && typeof raw === 'object' && Array.isArray((raw as { blocks?: unknown }).blocks)
      ? ((raw as { blocks: RawBlock[] }).blocks)
      : []);

  const blocks: ThemeBlock[] = [];
  for (const item of list) {
    if (!item || typeof item !== 'object' || typeof item.type !== 'string') continue;
    if (!KNOWN_TYPES.has(item.type)) continue;
    const props = item.props && typeof item.props === 'object' && !Array.isArray(item.props)
      ? (item.props as Record<string, unknown>)
      : {};
    blocks.push({ type: item.type, props });
  }
  return blocks;
}

export interface HtmlToBlocksResult {
  blocks: ThemeBlock[];
  usedAi: boolean;
  warning?: string;
  requestTokens: number;
  responseTokens: number;
}

/**
 * Convertit le HTML d'une page en blocs React via l'IA (xAI Grok).
 * Best-effort : en cas d'échec, renvoie un résultat vide avec avertissement.
 */
export async function convertHtmlToBlocks(
  html: string,
  opts: { userId?: number | null; pageLabel?: string },
): Promise<HtmlToBlocksResult> {
  const empty: HtmlToBlocksResult = { blocks: [], usedAi: false, requestTokens: 0, responseTokens: 0 };
  const source = (html || '').trim();
  if (!source) {
    return { ...empty, warning: 'Aucun contenu HTML à convertir.' };
  }

  let apiKey = '';
  try {
    const { data } = await supabase.from('zifek').select('xai_api_key').limit(1).maybeSingle();
    apiKey = (data?.xai_api_key as string) || '';
  } catch {
    apiKey = '';
  }
  if (!apiKey) {
    return { ...empty, warning: 'Aucune clé IA (xAI) configurée — la conversion nécessite une clé IA.' };
  }

  const startedAt = Date.now();
  try {
    const response = await fetch(XAI_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        model: XAI_MODEL,
        messages: [
          { role: 'system', content: buildSystemPrompt() },
          { role: 'user', content: buildUserContent(source, opts.pageLabel || '') },
        ],
        temperature: 0.3,
        max_tokens: 8000,
        response_format: { type: 'json_object' },
      }),
    });

    if (!response.ok) {
      const errText = await response.text();
      await logAiUsage({ provider: 'xai', model: XAI_MODEL, endpoint: XAI_ENDPOINT, durationMs: Date.now() - startedAt, status: 'error', userId: opts.userId ?? null });
      return { ...empty, warning: `Erreur IA ${response.status}: ${errText.slice(0, 160)}` };
    }

    const result = await response.json();
    const usage = readUsage(result?.usage);
    const content: string = result?.choices?.[0]?.message?.content || '';
    if (!content) {
      await logAiUsage({ provider: 'xai', model: XAI_MODEL, endpoint: XAI_ENDPOINT, ...usage, durationMs: Date.now() - startedAt, status: 'error', userId: opts.userId ?? null });
      return { ...empty, warning: 'Réponse vide de l\'IA.' };
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(content);
    } catch {
      const match = content.match(/```(?:json)?\s*([\s\S]*?)```/);
      if (!match) {
        await logAiUsage({ provider: 'xai', model: XAI_MODEL, endpoint: XAI_ENDPOINT, ...usage, durationMs: Date.now() - startedAt, status: 'error', userId: opts.userId ?? null });
        return { ...empty, warning: 'Impossible de lire la réponse JSON de l\'IA.' };
      }
      parsed = JSON.parse(match[1].trim());
    }

    const blocks = sanitizeBlocks(parsed);

    await logAiUsage({
      provider: 'xai',
      model: XAI_MODEL,
      endpoint: XAI_ENDPOINT,
      requestTokens: usage.requestTokens,
      responseTokens: usage.responseTokens,
      durationMs: Date.now() - startedAt,
      status: 'success',
      userId: opts.userId ?? null,
    });

    if (blocks.length === 0) {
      return { ...empty, usedAi: true, requestTokens: usage.requestTokens, responseTokens: usage.responseTokens, warning: 'L\'IA n\'a reconnu aucun bloc reconnu dans cette page.' };
    }

    return {
      blocks,
      usedAi: true,
      requestTokens: usage.requestTokens,
      responseTokens: usage.responseTokens,
      warning: htmlToText(source).length === 0 ? 'La page semble sans contenu textuel.' : undefined,
    };
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'erreur inconnue';
    await logAiUsage({ provider: 'xai', model: XAI_MODEL, endpoint: XAI_ENDPOINT, durationMs: Date.now() - startedAt, status: 'error', userId: opts.userId ?? null });
    return { ...empty, warning: `Conversion IA échouée (${msg}).` };
  }
}